import { useState } from 'react';
import { X, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { useAuth } from '../contexts/AuthContext';
import { Switch } from './ui/switch';
import { useCookieBannerVisible } from '../hooks/useCookieBannerVisible';

const AccessibilityPanel = () => {
  const [open, setOpen] = useState(false);
  const { settings, updateSetting } = useAccessibility();
  const { t } = useAuth();
  // Verberg de Aa-knop zolang de cookiebanner zichtbaar is, anders dekt-ie
  // de Accepteren/Weigeren-knoppen af op mobiel.
  const cookieBannerVisible = useCookieBannerVisible();

  const fontSizes = [
    { key: 'small', label: t('a11y.font_small') },
    { key: 'normal', label: t('a11y.font_normal') },
    { key: 'large', label: t('a11y.font_large') },
    { key: 'xlarge', label: t('a11y.font_xlarge') },
  ];

  const toggleSetting = (key, label) => {
    const next = !settings[key];
    updateSetting(key, next);
    toast.success(`${label}: ${next ? 'aan' : 'uit'}`);
  };

  return (
    <>
      {/* Floating toggle button */}
      <button
        onClick={() => setOpen(!open)}
        className={`fixed bottom-24 md:bottom-4 left-4 z-[9998] w-10 h-10 shadow-lg flex items-center justify-center hover:scale-110 focus-visible:outline-none transition-all active:scale-95 bg-hoi-blue ${
          cookieBannerVisible ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        aria-label={t('a11y.open_menu')}
        aria-hidden={cookieBannerVisible ? 'true' : undefined}
        tabIndex={cookieBannerVisible ? -1 : 0}
        data-testid="a11y-toggle"
      >
        <span className="w-full h-full flex items-center justify-center font-bold text-white text-base" style={{ fontFamily: 'serif' }}>Aa</span>
      </button>

      {/* Panel */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/30 z-[9998]"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-label={t('a11y.panel_title')}
            aria-modal="true"
            className="fixed bottom-20 md:bottom-20 left-4 right-4 sm:right-auto sm:w-80 z-[9999] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
            data-testid="a11y-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-[#1a1a2e] text-white">
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm">Aa</span>
                <h2 className="font-heading font-bold text-sm text-white">{t('a11y.panel_title')}</h2>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded-full hover:bg-white/20 transition-colors"
                aria-label={t('common.close')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-5">
              {/* Font Size */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <span className="text-base font-bold" aria-hidden="true">Aa</span>
                  {t('a11y.font_size')}
                </label>
                <div className="flex gap-1">
                  {fontSizes.map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => updateSetting('fontSize', key)}
                      className={`flex-1 py-2 px-1 rounded-lg text-xs font-medium transition-all ${
                        settings.fontSize === key
                          ? 'bg-[#FF9980] text-black font-bold'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      aria-pressed={settings.fontSize === key}
                      data-testid={`a11y-font-${key}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* High Contrast */}
              <div className="flex items-center justify-between">
                <label htmlFor="high-contrast" className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <Eye className="w-4 h-4" />
                  {t('a11y.high_contrast')}
                </label>
                <Switch
                  id="high-contrast"
                  checked={settings.highContrast}
                  onCheckedChange={() => toggleSetting('highContrast', t('a11y.high_contrast'))}
                  data-testid="a11y-contrast-toggle"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AccessibilityPanel;
